/**
 * KeyboardShortcutsSheet — the ⌘/ shortcuts panel.
 *
 * Every native Mac app publishes its shortcuts (Help → Keyboard Shortcuts);
 * an app that only *has* shortcuts hides them. This lists what the app
 * actually binds, with platform-correct modifiers (⌘ on Apple, Ctrl
 * elsewhere) and without advertising the installed-app-only section keys
 * while running in a browser tab, where they belong to the browser.
 */

import React from 'react';
import { X } from 'lucide-react';

import { modKeyLabel } from '../lib/platform';
import { isStandalone } from '../lib/pwa';
import { useFocusTrap } from '../hooks/useFocusTrap';

interface KeyboardShortcutsSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ShortcutGroup {
  title: string;
  items: { keys: string[]; label: string }[];
}

/** Event name that opens this sheet from anywhere (e.g. the ⌘K palette). */
export const SHOW_SHORTCUTS_EVENT = 'bbx:show-shortcuts';

export function buildShortcutGroups(mod: string, standalone: boolean): ShortcutGroup[] {
  const groups: ShortcutGroup[] = [
    {
      title: 'General',
      items: [
        { keys: [mod, 'K'], label: 'Open / close the command palette' },
        { keys: [mod, ','], label: 'Open Settings' },
        { keys: [mod, '/'], label: 'Show this shortcuts panel' },
        { keys: ['Esc'], label: 'Close the palette, panels, and menus' },
      ],
    },
    {
      title: 'Command palette',
      items: [
        { keys: ['↑', '↓'], label: 'Move between commands' },
        { keys: ['Enter'], label: 'Run the selected command' },
        { keys: ['Tab'], label: 'Cycle focus inside the palette' },
      ],
    },
    {
      title: 'Flow & agent canvas',
      items: [
        { keys: [mod, 'Z'], label: 'Undo the last canvas change' },
        { keys: [mod, '⇧', 'Z'], label: 'Redo' },
        { keys: [mod, 'S'], label: 'Save the draft' },
        { keys: [mod, 'Enter'], label: 'Publish (save and activate)' },
        { keys: [mod, 'D'], label: 'Duplicate the selected node' },
        { keys: [mod, '0'], label: 'Reset zoom to 100%' },
        { keys: ['Delete'], label: 'Delete the selected node' },
      ],
    },
  ];

  // Only real inside an installed app window — in a browser tab these keys
  // switch browser tabs, so listing them there would be a lie.
  if (standalone) {
    groups.splice(1, 0, {
      title: 'Go to section',
      items: [
        { keys: [mod, '1'], label: 'Business Hub' },
        { keys: [mod, '2'], label: 'Automations' },
        { keys: [mod, '3'], label: 'Workspace' },
        { keys: [mod, '4'], label: 'Calendar' },
        { keys: [mod, '5'], label: 'Inbox' },
        { keys: [mod, '6'], label: 'Account' },
      ],
    });
  }

  return groups;
}

export const KeyboardShortcutsSheet: React.FC<KeyboardShortcutsSheetProps> = ({
  isOpen,
  onClose,
}) => {
  const dialogRef = useFocusTrap(isOpen, onClose);

  if (!isOpen) return null;

  const groups = buildShortcutGroups(modKeyLabel(), isStandalone());

  return (
    <div
      className="fixed inset-0 bg-gray-950/25 backdrop-blur-sm z-command-bar flex items-start justify-center pt-24 bbx-scrim-in"
      onClick={onClose}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Keyboard shortcuts"
        className="w-full max-w-2xl bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden bbx-palette-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-900">Keyboard shortcuts</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-900 transition-colors"
            aria-label="Close keyboard shortcuts"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 max-h-[70vh] overflow-y-auto grid gap-6 sm:grid-cols-2">
          {groups.map((group) => (
            <section key={group.title}>
              <h3 className="text-xs uppercase tracking-wide font-medium text-gray-400 mb-2">
                {group.title}
              </h3>
              <ul className="space-y-1.5">
                {group.items.map((item) => (
                  <li key={item.label} className="flex items-center justify-between gap-4">
                    <span className="text-sm text-gray-600">{item.label}</span>
                    <span className="flex items-center gap-1 flex-shrink-0">
                      {item.keys.map((key) => (
                        <kbd
                          key={key}
                          className="px-1.5 py-0.5 min-w-[1.5rem] text-center bg-gray-50 border border-gray-200 rounded text-xs font-medium text-gray-600"
                        >
                          {key}
                        </kbd>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};
